import type { ReactNode } from 'react';
import DOMPurify from 'dompurify';

import Box from '@mui/material/Box';

/**
 * A message composed by the backend may be plain text or may carry its own markup.
 * Which one it is, is not ours to predict, so nothing here looks for a particular tag
 * or format: the string is sanitised and whatever survives is rendered.
 *
 * Sanitised rather than trusted because the text is composed server-side and can carry
 * anything. `target` is allowed through rather than added: whether a link leaves the app
 * belongs to whoever wrote the message, and the default policy would strip the attribute
 * instead of leaving that choice alone. A caller that has to stay on its page — a status shown
 * inside a settings screen — opts into `newTab`, which opens every link in a new tab.
 */
export const htmlMessage = (
  text: string,
  { newTab = false }: { newTab?: boolean } = {},
): { content: ReactNode; hasLink: boolean } => {
  const container = document.createElement('div');
  container.append(DOMPurify.sanitize(text, { ADD_ATTR: ['target'], RETURN_DOM_FRAGMENT: true }));
  if (newTab) {
    container.querySelectorAll('a[href]').forEach((link) => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });
  }

  // Both questions are put to the parsed document, never to the string, so no format has
  // to be anticipated. Only something actionable needs the user's time, and after
  // sanitising an anchor with an address is the only thing left that can be acted on.
  const hasLink = container.querySelector('a[href]') !== null;

  // No element at all means plain text, which keeps rendering exactly as it does today.
  if (!container.childElementCount) return { content: text, hasLink };

  return {
    // A bare span with no typography of its own: markup then inherits exactly what a
    // plain string child inherits, so the two render identically apart from the markup.
    content: (
      <Box
        component="span"
        sx={{ overflowWrap: 'anywhere', '& a': { color: 'inherit' } }}
        dangerouslySetInnerHTML={{ __html: container.innerHTML }}
      />
    ),
    hasLink,
  };
};

export default htmlMessage;
